import { useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { getUpdateRequired, subscribeUpdateRequired } from '@/lib/updateRequired';
import { useBlockingLayer } from '@/hooks/useBlockingLayer';
import { Button } from '@/components/ui/button';

/**
 * What the WEB does when a response carries the update-required SQLSTATE
 * (KA001, src/lib/updateRequired.ts). The interceptor lives in the shared
 * Supabase client, so it runs in the browser too (Codex round 135).
 *
 * A browser's remedy is a reload, not an app store: a stale tab picks up the
 * current deployment when it reloads. So this is a reload prompt, and it never
 * links to Google Play — that screen is native-only and does not exist in the
 * web bundle.
 *
 * Inert today: no RPC returns KA001 before M7's retirement migration, so this
 * renders nothing for every current user.
 *
 * When it does show, it is actually modal (Codex, #1084): it renders into
 * <body>, the page behind it is inert, and Reload has focus — so the obsolete
 * client cannot keep acting through a control that was focused underneath.
 */
export function WebUpdateRequiredPrompt() {
  const reason = useSyncExternalStore(subscribeUpdateRequired, getUpdateRequired, getUpdateRequired);
  if (!reason) return null;
  return <BlockingReloadPrompt />;
}

function BlockingReloadPrompt() {
  const root = useRef<HTMLDivElement>(null);
  const reload = useRef<HTMLButtonElement>(null);
  useBlockingLayer(root, reload);
  return createPortal(
    <div
      ref={root}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="web-update-title"
      aria-describedby="web-update-body"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-background/90 px-6"
    >
      <div className="max-w-sm rounded-2xl border bg-card p-6 text-center shadow-lg">
        <h2 id="web-update-title" className="text-lg font-semibold text-foreground">
          Kanfy has been updated
        </h2>
        <p id="web-update-body" className="mt-2 text-sm text-foreground">
          This page is out of date and can no longer complete that action. Reload to get the latest version.
        </p>
        <Button ref={reload} className="mt-4 w-full" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    </div>,
    document.body,
  );
}
